import React, { useMemo, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactIconText from 'shared/design-system/components/TrueImpactIconText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { mergeClassNames, mergeKeyNames } from 'shared/utils/mergeClassNames';
import StagesModal from '../../../../ReportBuilder/Components/ImpactIndicator/Stages/StagesModal';
import {
  IndicatorSectionKey,
  IndicatorWithUpdate,
  IndicatorByService,
} from '../../../../ReportBuilder/Components/ImpactIndicator/utils';
import {
  IndicatorDetails,
  SectionIndicators,
} from '../../../../ReportBuilder/Components/IndicatorDetails/utils';
import ImpactDeleteStage from './ImpactDeleteStage';
import ImpactEditable from './ImpactEditable';
import { HandleDeleteIndicatorData } from './ImpactTableStage';
import { faTrash } from '@fortawesome/pro-light-svg-icons';
import { FeedbackCommentBodyWithIndex } from '../../../utils';
import ShowIndicatorFeedback from './ShowIndicatorFeedback';
import { parseFeedbackModelDetails } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackCommentTextarea';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';
import ImpactComments from './ImpactComments';
import {
  ErrorsByIndicatorId,
  HandleUpdatePropertyData,
  ModelSaveWarning,
} from './modelErrors';
import ImpactForecastWrapper from './ImpactForecastWrapper';
import { replaceMadLib } from '../../../../ReportBuilder/Components/ImpactIndicator/IndicatorCardDetails';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { useReport } from 'shared/providers/ReportProvider';
import { capitalizeFirstLetter } from 'shared/utils/stringUtils';

const getUniqueIndicators = (
  allIndicators: IndicatorByService,
  indicators: IndicatorByService,
  sectionKey: IndicatorSectionKey,
) => {
  const result: IndicatorWithUpdate[] = [];
  for (const i of allIndicators[sectionKey]) {
    if (indicators[sectionKey].findIndex(i2 => i2.id === i.id) === -1) {
      result.push(i);
    }
  }
  return result;
};

interface ImpactStageRenderProps {
  isSucceed: boolean;
  impactSummaryIndex: number;
  lastIndex: number;
  sectionKey: IndicatorSectionKey;
  stageName: string;
  isMoreThanOne: boolean;
  reportId: number;
  indicator: HandleDeleteIndicatorData;
  allIndicators: IndicatorByService;
  errorsByIndicator: ErrorsByIndicatorId;
  warningsByIndicator: ErrorsByIndicatorId;
  feedback: FeedbackCommentBodyWithIndex[];
  indicatorById: { index: number; indicator: IndicatorDetails };
  handleClose: () => void;
  handleOpen: () => void;
  handleAdd: (indicator: IndicatorWithUpdate) => void;
  handleDeleteIndicator: (v: HandleDeleteIndicatorData) => unknown;
  handleDeleteSection: (sectionKey: IndicatorSectionKey) => unknown;
  handleUpdateProperty: (data: HandleUpdatePropertyData) => unknown;
  open: boolean;
  isReportPassed: boolean;
  impactSummary: IndicatorByService;
  impactDetails: SectionIndicators;
  onEditChange: (warning: ModelSaveWarning, isEditing: boolean) => unknown;
}

const ImpactStageRender: React.FC<ImpactStageRenderProps> = ({
  isSucceed,
  impactSummaryIndex,
  lastIndex,
  handleDeleteSection,
  sectionKey,
  stageName,
  isMoreThanOne,
  reportId,
  indicator,
  warningsByIndicator,
  handleUpdateProperty,
  indicatorById,
  handleDeleteIndicator,
  handleAdd,
  open,
  handleClose,
  handleOpen,
  impactSummary,
  allIndicators,
  isReportPassed,
  errorsByIndicator,
  feedback,
  onEditChange,
}) => {
  const {
    impactDetails,
    reachHumanIndicator,
    service,
    reportPeriod,
    intermediaries,
    beneficiaryName,
  } = useIndicator();
  const { programName } = useReport();
  const [showSectionComments, setShowSectionComments] = useState<boolean>(true);

  const { sectionComments, indicatorComments } = useMemo(() => {
    const sectionComments: FeedbackCommentBodyWithIndex[] = [];
    const indicatorComments: FeedbackCommentBodyWithIndex[] = [];
    feedback.forEach(f => {
      const details = parseFeedbackModelDetails(f.details);
      if (details.indicatorId === indicator.indicatorId) {
        indicatorComments.push(f);
      } else if (
        details.sectionId === sectionKey &&
        details.indicatorId === undefined
      ) {
        sectionComments.push(f);
      }
    });
    return { sectionComments, indicatorComments };
  }, [feedback, sectionKey, indicator]);

  const errors = errorsByIndicator[indicator.indicatorId] || {};
  const warnings = warningsByIndicator[indicator.indicatorId] || {};
  const indicators = getUniqueIndicators(
    allIndicators,
    impactSummary,
    sectionKey,
  );

  const madLib = useMemo(() => {
    const text = indicator.impactSummaryIndicator.impactReceiptText;
    const defaultText = indicator.impactDetailIndicator.indicatorDefinition;
    const madLib = text
      ? replaceMadLib(
          text,
          programName,
          beneficiaryName,
          intermediaries,
          isReportPassed,
          reportPeriod,
          service,
          reachHumanIndicator,
          impactDetails.learn[0],
          impactDetails.act[0],
          indicator.impactDetailIndicator.forecast,
          indicator.impactDetailIndicator.finalForecast,
          indicator.impactSummaryIndicator,
        )
      : undefined;
    return typeof madLib === 'string'
      ? capitalizeFirstLetter(madLib)
      : madLib || defaultText;
  }, [
    indicator,
    isReportPassed,
    programName,
    impactDetails,
    reachHumanIndicator,
    service,
    reportPeriod,
    intermediaries,
    beneficiaryName,
  ]);

  return (
    <>
      <div
        className={mergeClassNames(
          'stage-column',
          isSucceed && impactSummaryIndex === lastIndex && 'bottom-left-radius',
        )}
      >
        {impactSummaryIndex === 0 && (
          <ImpactDeleteStage
            stageName={stageName}
            sectionKey={sectionKey}
            sectionComments={sectionComments}
            showSectionComments={showSectionComments}
            setShowSectionComments={setShowSectionComments}
            handleDeleteSection={handleDeleteSection}
            className="delete-stage-button"
          />
        )}
      </div>
      <div
        key={`${indicator.indicatorId}-${impactSummaryIndex}`}
        className={mergeClassNames(
          'metric-column',
          isMoreThanOne &&
            impactSummaryIndex !== lastIndex &&
            'metric-column-border',
        )}
      >
        {impactSummaryIndex === 0 && sectionComments.length > 0 && (
          <TrueImpactCollapse show={showSectionComments}>
            <ImpactComments
              feedback={sectionComments}
              className="impact-model-comments"
            />
          </TrueImpactCollapse>
        )}
        <TrueImpactText className="definition-text">{madLib}</TrueImpactText>
        <ImpactEditable
          reportId={reportId}
          sectionKey={sectionKey}
          indicatorKey="impactSummary"
          indicator={indicator}
          hideFile={true}
          name="updatedText"
          title="What it means to your program"
          isError={errors.updatedText || false}
          isWarning={warnings.updatedText || false}
          value={indicator.impactSummaryIndicator.updatedText}
          handleSave={handleUpdateProperty}
          className="editable-text editable-text-margin"
          onEditChange={onEditChange}
        />
        {sectionKey === 'succeed' && (
          <ImpactEditable
            reportId={reportId}
            sectionKey={sectionKey}
            indicatorKey="impactDetail"
            indicator={indicator}
            hideFile={true}
            name="timePeriod"
            title="How long will it take for this impact to be realized?"
            isError={errors.timePeriod || false}
            isWarning={warnings.timePeriod || false}
            value={indicator.impactDetailIndicator.timePeriod}
            handleSave={handleUpdateProperty}
            className="editable-text editable-text-margin"
            onEditChange={onEditChange}
          />
        )}
        <ImpactEditable
          reportId={reportId}
          sectionKey={sectionKey}
          indicatorKey="impactDetail"
          indicator={indicator}
          hideFile={true}
          name="measurement"
          title="Measurement type"
          isError={errors.measurement || false}
          isWarning={warnings.measurement || false}
          value={indicator.impactDetailIndicator.measurement}
          handleSave={handleUpdateProperty}
          className="editable-text editable-text-margin"
          onEditChange={onEditChange}
        />
        <ImpactEditable
          reportId={reportId}
          sectionKey={sectionKey}
          indicatorKey="impactDetail"
          indicator={indicator}
          name="file"
          title="Measurement sources and assumptions"
          value={undefined}
          file={indicator.impactDetailIndicator.file}
          isError={errors.sourcesAndAssumptions || false}
          isWarning={warnings.file || errors.sourcesAndAssumptions || false}
          handleSave={handleUpdateProperty}
          className="editable-text"
          onEditChange={onEditChange}
        />
        <ShowIndicatorFeedback comments={indicatorComments} />
        <TrueImpactIconText
          label="Delete Indicator"
          icon={faTrash}
          className="delete-button"
          onClick={() => handleDeleteIndicator(indicator)}
        />
        {impactSummaryIndex === lastIndex && indicators.length > 0 && (
          <StagesModal
            open={open}
            header={stageName}
            sectionKey={sectionKey}
            indicators={indicators}
            marginTop={SPACERS.small}
            handleAdd={handleAdd}
            handleOpen={handleOpen}
            handleClose={handleClose}
            selectedIndicators={[]}
            className="add-indicator-modal"
          />
        )}
      </div>
      <div
        key={mergeKeyNames('forecast', sectionKey, indicator.indicatorId)}
        className={mergeClassNames(
          'results-column',
          isSucceed &&
            impactSummaryIndex === lastIndex &&
            'bottom-right-radius',
        )}
      >
        <ImpactForecastWrapper
          reportId={reportId}
          indicator={indicator}
          sectionKey={sectionKey}
          indicatorById={indicatorById}
          isReportPassed={isReportPassed}
          isForecastError={errors.forecast || false}
          isForecastWarning={warnings.forecast || false}
          isFinalForecastError={errors.finalForecast || false}
          isFinalForecastWarning={warnings.finalForecast || false}
          onEditChange={onEditChange}
        />
      </div>
    </>
  );
};

export default ImpactStageRender;
